/**
 * Cart Drawer Functionality
 */
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    if (!this.drawer) return;
    
    this.body = document.getElementById('cart-drawer-body');
    this.form = document.getElementById('cart-drawer-form');
    
    // This notice never expires or clears a shopper's cart.
    this.notice = this.drawer.querySelector('[data-cart-drop-notice]');
    try { sessionStorage.removeItem('cart_timer_data'); } catch (error) {}

    this.init();
  }

  init() {
    // Open drawer
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-cart-drawer-open]');
      if (!trigger) return;
      e.preventDefault();     
      e.stopPropagation();
      this.open();
    });

    // Close drawer events
    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-active')) {
        this.close();
      }
    });

    // Quantity controls
    this.drawer.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-item-increase]')) {
        this.updateQuantity(e.target.dataset.line, 1);
      } else if (e.target.matches('[data-cart-item-decrease]')) {
        this.updateQuantity(e.target.dataset.line, -1);
      } else if (e.target.matches('[data-cart-item-remove]')) {
        this.removeItem(e.target.dataset.line);
      }
    });

    // Quantity input change
    this.drawer.addEventListener('change', (e) => {
      if (e.target.classList.contains('cart-drawer__qty-input')) {
        const newQty = parseInt(e.target.value);
        const line = e.target.dataset.line;
        this.changeQuantity(line, newQty);
      }
    });

    // Intercept add to cart forms
    this.interceptAddToCart();
    window.addEventListener('resize', () => this.resizeDrawer());
    window.visualViewport?.addEventListener('resize', () => this.resizeDrawer());
  }

  resizeDrawer() {
    if (this.drawer.classList.contains('is-active')) {
      const height = Math.round(window.visualViewport?.height || window.innerHeight);
      this.drawer.querySelector('.cart-drawer__container').style.height = height + 'px';
      const main = this.drawer.querySelector('.div-drawer__main-content');
      // Flex layout reserves space for the fixed header and footer on every screen.
      main.style.height = '100%';
    }
  }

  open() {
    if (!this.drawer.classList.contains('is-active')) {
      this.pageScrollY = window.scrollY;
      document.body.style.setProperty('--cart-scroll-top', -this.pageScrollY + 'px');
      document.documentElement.classList.add('cart-drawer-open');
      document.body.classList.add('cart-drawer-open');
    }
    this.drawer.classList.add('is-active');
    this.resizeDrawer();
    this.drawer.querySelector('.div-drawer__main-content').scrollTop = 0;
    this.body.scrollTop = 0;
    this.drawer.querySelector('.cart-drawer__close').focus({ preventScroll: true });
  }

  close() {
    if (!this.drawer.classList.contains('is-active')) return;
    this.drawer.classList.remove('is-active');
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-drawer-open');
    document.body.style.removeProperty('--cart-scroll-top');
    window.scrollTo({ top: this.pageScrollY || 0, behavior: 'instant' });
  }

  async updateQuantity(line, change) {
    const input = this.drawer.querySelector(`input[data-line="${line}"]`);
    const currentQty = parseInt(input.value);
    const newQty = Math.max(0, currentQty + change);
    
    await this.changeQuantity(line, newQty);
  }

  async changeQuantity(line, quantity) {
    try {
      this.setLoading(true);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Cart update failed');

      await this.refreshCart();
      this.updateHeaderCart();
      
    } catch (error) {
      console.error('Error updating cart:', error);
      alert('There was an error updating your cart. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async removeItem(line) {
    await this.changeQuantity(line, 0);
  }

  async refreshCart() {
    const response = await fetch('/cart.js', { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch cart');
    const cart = await response.json();
    // Liquid exposes all Shopify allocations, including order-level discounts.
    // Reuse that authoritative markup instead of estimating a discount in JS.
    let renderedBody = null;
    if (cart.item_count > 0 && this.drawer.dataset.sectionId) {
      try {
        const sectionUrl = new URL(window.location.href);
        sectionUrl.searchParams.set('section_id', this.drawer.dataset.sectionId);
        const sectionResponse = await fetch(sectionUrl, { cache: 'no-store' });
        if (!sectionResponse.ok) throw new Error('Cart price section unavailable');
        const sectionDocument = new DOMParser().parseFromString(await sectionResponse.text(), 'text/html');
        const sectionBody = sectionDocument.getElementById('cart-drawer-body');
        if (sectionBody && sectionBody.querySelectorAll('.cart-drawer__item').length === cart.items.length) {
          renderedBody = sectionBody.innerHTML;
        }
      } catch (error) {
        console.warn('Cart discount display could not refresh:', error);
      }
    }
    this.rebuildCartHTML(cart, renderedBody);
    return cart;
  }

  syncRecommendations(cart) {
    // Keep both layouts in sync, including cards hidden when the page loaded.
    const productIds = new Set(cart.items.map(item => String(item.product_id)));
    this.drawer.querySelectorAll('.cart-drawer__recommendations, .cart-drawer__recommendations_mobile').forEach(panel => {
      const cards = Array.from(panel.querySelectorAll('[data-cart-recommendation-product-id]'));
      cards.forEach(card => {
        card.hidden = cart.item_count === 0 || productIds.has(card.dataset.cartRecommendationProductId);
      });
      panel.hidden = !cards.some(card => !card.hidden);
    });
  }

  rebuildCartHTML(cart, renderedBody = null) {
    this.syncRecommendations(cart);
    if (this.notice) this.notice.hidden = cart.item_count === 0;
    this.body.innerHTML = '';
    
    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your cart is empty</p>
          <a href="/collections/classica-collection" class="button">Continue Shopping</a>
        </div>
      `;
      
      const footer = this.drawer.querySelector('.cart-drawer__footer');
      if (footer) footer.remove();
      this.resizeDrawer();
      
      return;
    }
    
    if (renderedBody !== null) {
      this.body.innerHTML = renderedBody;
      this.form = this.body.querySelector('#cart-drawer-form');
      this.updateFooter(cart);
      this.resizeDrawer();
      return;
    }

    let itemsHTML = '<ul class="cart-drawer__items">';
    
    cart.items.forEach((item, index) => {
      const lineNumber = index + 1;
      
      itemsHTML += `
        <li class="cart-drawer__item">
          <div class="cart-drawer__item-image">
            <img src="${item.image}" alt="${item.title}">
          </div>
          <div class="cart-drawer__item-details">
            <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
            ${item.variant_title ? `<div class="cart-drawer__item-variant">${item.variant_title}</div>` : ''}
            <div class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</div>
            
            <div class="cart-drawer__item-quantity">
              <button type="button" class="cart-drawer__qty-btn" data-cart-item-decrease data-line="${lineNumber}">−</button>
              <input type="number" class="cart-drawer__qty-input" value="${item.quantity}" min="0" data-line="${lineNumber}">
              <button type="button" class="cart-drawer__qty-btn" data-cart-item-increase data-line="${lineNumber}">+</button>
            </div>
            
            <button type="button" class="cart-drawer__item-remove" data-cart-item-remove data-line="${lineNumber}">Remove</button>
          </div>
        </li>
      `;
    });
    
    itemsHTML += '</ul>';
    this.body.innerHTML = itemsHTML;
    
    this.updateFooter(cart);
    this.resizeDrawer();
  }

  updateFooter(cart) {
    let footer = this.drawer.querySelector('.cart-drawer__footer');
    
    const iconsHTML = `
    <ul
            class="side-cart__icons-list list-none"
            style="margin-bottom: 15px; display: flex; gap: 15px; font-size: 0.9w; color: #7D7978; margin: 0 -16px 0 -16px; container-type: inline-size; padding: 0 1vw;"
          >
            <li style="display: flex; align-items: center; gap: 1.5vh; flex-direction: column; justify-content: inherit;">
              <img
                src="https://cdn.shopify.com/s/files/1/0669/4282/8783/files/HOURGLASS_LIMITED_DROP_ICON_BLACK.png?v=1771764104"
                width="32px"
                height="32px"
              >
              Limited Drop
            </li>
            <li style="display: flex; align-items: center; gap: 0.8vh; flex-direction: column; justify-content: inherit;">
              <img
                src="https://cdn.shopify.com/s/files/1/0669/4282/8783/files/FREE_AUS_SHIP_ICON_d1452c0c-63f3-4adf-9e37-49a5c66bf414.png?v=1771764104"
                height="32px"
                width="60px"
              >
              Free AUS Shipping
            </li>
            <li style="display: flex; align-items: center; gap: 2.75vh; flex-direction: column; justify-content: inherit;">
              <img
                src="https://cdn.shopify.com/s/files/1/0669/4282/8783/files/6a842fc-Afterpay_Badge_BlackonMint-removebg-preview.png?v=1771764104"
                height="32px"
                width="60px"
              >
              Pay Later With Afterpay
            </li>
          </ul>
  `;

    const footerHTML = `
    ${iconsHTML}
      <div class="cart-drawer__subtotal">
        <span class="cart-drawer__subtotal-label">Subtotal</span>
        <span class="cart-drawer__subtotal-amount">${this.formatMoney(cart.total_price)}</span>
      </div>
      
      ${cart.cart_level_discount_applications.length > 0 ? `
        <ul class="cart-drawer__discounts">
          ${cart.cart_level_discount_applications.map(discount => `
            <li class="cart-drawer__discount">
              <span>${discount.title}</span>
              <span>-${this.formatMoney(discount.total_allocated_amount)}</span>
            </li>
          `).join('')}
        </ul>
      ` : ''}
      
      <div class="cart-drawer__taxes">FREE Standard Shipping Australia Wide</div>
      
      <a href="/checkout" class="button cart-drawer__checkout" style="text-align:center;">Check out</a>
      <a href="/cart" class="cart-drawer__view-cart">Shopping Cart</a>
    `;
    
    if (footer) {
      footer.innerHTML = footerHTML;
    } else {
      footer = document.createElement('div');
      footer.className = 'cart-drawer__footer';
      footer.innerHTML = footerHTML;
      this.drawer.querySelector('.div-drawer__main-content').appendChild(footer);
    }
  }

  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  updateHeaderCart() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      });
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.body.classList.add('is-loading');
    } else {
      this.body.classList.remove('is-loading');
    }
  }

  interceptAddToCart() {
    // Own these AJAX submissions before the theme's legacy form listener.
    document.addEventListener('submit', async (e) => {
      if (!e.target.matches('form[action*="/cart/add"]') ||
          e.target.closest('[data-ajax-add-to-cart="false"]')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      await this.addToCart(e.target);
    }, true);

    document.addEventListener('theme:cartchanged', async () => {
      try {
        await this.refreshCart();
        this.open();
      } catch (error) {
        console.error('Unable to refresh cart:', error);
        window.location.assign('/cart');
      }
    });
  }

  async addToCart(form) {
    if (!this.pendingAddForms) this.pendingAddForms = new WeakSet();
    if (this.pendingAddForms.has(form)) return;
    this.pendingAddForms.add(form);
    const formData = new FormData(form);
    const buttons = Array.from(form.querySelectorAll('[type="submit"]'));
    const disabledStates = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    let itemAdded = false;
    try {
      const response = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }
      itemAdded = true;
      await this.refreshCart();
      this.updateHeaderCart();
      document.dispatchEvent(new CustomEvent('cart:item-added'));
      this.open();
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (itemAdded) {
        // A failed preview refresh must never invite a duplicate purchase attempt.
        window.location.assign('/cart');
      } else {
        alert(error.message || 'There was an error adding the item to your cart.');
      }
    } finally {
      buttons.forEach((button, index) => { button.disabled = disabledStates[index]; });
      form.removeAttribute('aria-busy');
      this.pendingAddForms.delete(form);
    }
  }


}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new CartDrawer();
  });
} else {
  window.cartDrawer = new CartDrawer();
}